import { useErrorInlineNotification } from '@/components/ErrorInlineNotification';
import { useIpAddressWhitelistIpRules } from '@/features/ip_address_whitelists/hooks/useIpAddressWhitelistIpRules';
import { useErrorTrackingSystem } from '@/hooks/useErrorTrackingSystem';
import { useTranslation } from '@/i18n';
import {
  ButtonGroup,
  ButtonV2,
  Form,
  FormInput,
  Modal,
} from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { FC, memo, useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import styles from './IpAddressWhitelistUpsertModal.module.scss';

const cx = classnames.bind(styles);

const FORM_ID = 'ip_address_whitelist_add_form';

export type IpAddressWhitelistUpsertFormValues = {
  ip_address: string;
};

export type IpAddressWhitelistUpsertSubmitResult = {
  errorMessage?: string;
};

export type IpAddressWhitelistUpsertModalProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    values: IpAddressWhitelistUpsertFormValues
  ) => Promise<IpAddressWhitelistUpsertSubmitResult | void>;
};

export const IpAddressWhitelistUpsertModal: FC<IpAddressWhitelistUpsertModalProps> =
  memo(({ open, onClose, onSubmit }) => {
    const { t } = useTranslation();
    const { send } = useErrorTrackingSystem();
    const [
      { setError },
      ErrorContextHolder,
      ErrorInlineNotificationProvider,
      onClearMessage,
    ] = useErrorInlineNotification();

    const methods = useForm<IpAddressWhitelistUpsertFormValues>({
      mode: 'onChange',
      defaultValues: {
        ip_address: '',
      },
    });

    const { control, reset, formState } = methods;

    useEffect(() => {
      if (!open) return;
      reset({ ip_address: '' });
      onClearMessage();
    }, [onClearMessage, open, reset]);

    const ipRules = useIpAddressWhitelistIpRules();

    const ipLabelProps = useMemo(
      () => ({
        text: t('ip_address_whitelist_modal_ip_label'),
      }),
      [t]
    );

    const InputProps = useMemo(
      () => ({
        placeholder: '10.0.0.1',
      }),
      []
    );

    const onValidSubmit = useCallback(
      async (values: IpAddressWhitelistUpsertFormValues) => {
        try {
          const result = await onSubmit({
            ip_address: values.ip_address.trim(),
          });
          if (result?.errorMessage) {
            setError(new Error(result.errorMessage));
          } else {
            onClearMessage();
          }
        } catch (error: unknown) {
          if (error instanceof Error) {
            send(error);
          } else {
            send(new Error(String(error)));
          }
          setError(new Error(t('system_error')));
        }
      },
      [onClearMessage, onSubmit, send, setError, t]
    );

    const helperTitle = t('ip_address_whitelist_ip_helper_title');
    const helperDirect = t('ip_address_whitelist_ip_helper_direct');
    const helperCidr = t('ip_address_whitelist_ip_helper_cidr');

    return (
      <ErrorInlineNotificationProvider value={setError}>
        <Modal
          mask
          onClose={onClose}
          open={open}
          title={t('ip_address_whitelist_modal_add_new_title')}
          size='md'
          testId='ip-address-whitelist-add-modal'
          footer={
            <ButtonGroup>
              <ButtonV2
                size='sm'
                onClick={onClose}
                isSecondary
                isDisabled={formState.isSubmitting}
              >
                {t('cancel')}
              </ButtonV2>
              <ButtonV2
                size='sm'
                type='submit'
                form={FORM_ID}
                isDisabled={
                  formState.isSubmitting ||
                  !formState.isValid ||
                  ErrorContextHolder.props.visible
                }
                isLoading={formState.isSubmitting}
              >
                {t('ip_address_whitelist_modal_add_submit')}
              </ButtonV2>
            </ButtonGroup>
          }
        >
          <div className={cx('body')}>
            <Form<IpAddressWhitelistUpsertFormValues>
              id={FORM_ID}
              formMethod={methods}
              onSubmit={onValidSubmit}
              testId={FORM_ID}
            >
              <div className={cx('error-notification')}>
                {ErrorContextHolder}
              </div>
              <div className={cx('fields')}>
                <div className={cx('field')}>
                  <FormInput<IpAddressWhitelistUpsertFormValues>
                    control={control}
                    name='ip_address'
                    inputId='ip_address_whitelist_ip'
                    testId='ip_address_whitelist_ip'
                    rules={ipRules}
                    label={ipLabelProps}
                    inputProps={InputProps}
                  />
                </div>
                <div className={cx('helper')}>
                  <p className={cx('helper-title')}>{helperTitle}</p>
                  <p className={cx('helper-item')}>{helperDirect}</p>
                  <p className={cx('helper-item')}>{helperCidr}</p>
                </div>
              </div>
            </Form>
          </div>
        </Modal>
      </ErrorInlineNotificationProvider>
    );
  });
IpAddressWhitelistUpsertModal.displayName = 'IpAddressWhitelistUpsertModal';
